import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { SnackbarVariant } from './Snackbar';
import { adminApi, type PortfolioDto } from '../services/adminApi';
import { getStockQuote, searchStocks } from '../services/stockApi';
import stocksList from '../data/stocksList.json';
import { useEscapeKey } from '../hooks/useEscapeKey';

type StockListItem = {
  symbol: string;
  name: string;
  exchange: string;
  exchangeName?: string;
  category: string;
};

const getExchangeSearchPriority = (exchangeRaw: string | undefined): number => {
  const exchange = String(exchangeRaw || '').trim().toUpperCase();
  if (exchange === 'NASDAQ' || exchange === 'NASDAQGS' || exchange === 'NASDAQGM' || exchange === 'NASDAQCM') return 0;
  if (exchange === 'NYSE') return 1;
  if (exchange === 'AMEX' || exchange === 'NYSEAMERICAN' || exchange === 'NYSE AMERICAN') return 2;
  return 3;
};

export type QuickAddPortfolioHoldingAdded = {
  holdingId: number;
  portfolioName: string;
};

type Props = {
  isDarkMode: boolean;
  open: boolean;
  prefillSymbol: string | null;
  authToken?: string | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
  onClose: () => void;
  onAdded: (info: QuickAddPortfolioHoldingAdded) => void;
};

const normalizeSymbol = (raw: string): string => String(raw || '').trim().toUpperCase();
const normalizeSearchSymbolInput = (raw: string): string => {
  const normalized = normalizeSymbol(raw).replace(/\s+/g, ' ');
  const classShareMatch = normalized.match(/^([A-Z]{1,5})\s+([A-Z])$/);
  if (classShareMatch) return `${classShareMatch[1]}.${classShareMatch[2]}`;
  return normalized;
};

const getTodayInputDate = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const inputDateToUtcIso = (raw: string): string | null => {
  const t = String(raw || '').trim();
  if (!t) return null;
  const parts = t.split('-');
  if (parts.length !== 3) return null;
  const [yStr, mStr, dStr] = parts;
  const y = Number(yStr);
  const m = Number(mStr);
  const d = Number(dStr);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (y < 1900 || y > 3000) return null;
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > 31) return null;
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0)).toISOString();
};

const parseNumOrNull = (raw: string): number | null => {
  const t = String(raw ?? '').trim();
  if (!t) return null;

  let cleaned = t.replace(/\s+/g, '');
  cleaned = cleaned.replace(/[$₪€£]/g, '');

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  if (hasComma && !hasDot) cleaned = cleaned.replace(',', '.');
  cleaned = cleaned.replace(/,/g, '');

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const isUnauthorized = (e: any): boolean => {
  const status = Number(e?.status);
  return status === 401;
};

const QuickAddPortfolioHoldingModal: React.FC<Props> = ({
  isDarkMode,
  open,
  prefillSymbol,
  authToken,
  onUnauthorized,
  onNotify,
  onClose,
  onAdded,
}) => {
  useEscapeKey(Boolean(open), onClose);

  const colors = useMemo(() => {
    const dark = isDarkMode;
    return {
      cardBorder: dark ? '#1f2a44' : '#e2e8f0',
      shadow: dark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 10px 30px rgba(15, 23, 42, 0.08)',
      hoverBg: dark ? '#0b1730' : '#f1f5f9',
      text: dark ? '#e5e7eb' : '#0f172a',
    };
  }, [isDarkMode]);

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '2rem',
  };

  const addHoldingModalStyle: React.CSSProperties = {
    width: 'min(460px, 96vw)',
    maxHeight: '90vh',
    backgroundColor: '#FFFFFF',
    borderRadius: 0,
    boxShadow: colors.shadow,
    border: `1px solid ${colors.cardBorder}`,
    padding: '14px 20px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    opacity: 1,
    overflowY: 'auto',
  };

  const addHoldingTextStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '100%',
    letterSpacing: 0,
    color: '#0D0D0D',
  };

  const addHoldingLabelStyle: React.CSSProperties = {
    ...addHoldingTextStyle,
    display: 'block',
  };

  const addTitleTextStyle: React.CSSProperties = {
    fontFamily: addHoldingTextStyle.fontFamily,
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: 20,
    lineHeight: '100%',
    letterSpacing: 0,
    color: '#000000',
  };

  const addHoldingInputStyle: React.CSSProperties = {
    ...addHoldingTextStyle,
    width: 220,
    minWidth: 220,
    maxWidth: 220,
    height: 40,
    borderRadius: 8,
    border: '1px solid #D9D9D9',
    padding: '10px 14px',
    outline: 'none',
    backgroundColor: '#FFFFFF',
    boxSizing: 'border-box',
    opacity: 1,
  };

  const addHoldingSearchButtonStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '100%',
    letterSpacing: 0,
    width: 120,
    height: 37,
    borderRadius: 6,
    padding: '4px 14px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#0D0D0D',
    color: '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
    opacity: 1,
    flex: '0 0 auto',
  };

  const addHoldingAddButtonStyle: React.CSSProperties = {
    fontFamily: addHoldingTextStyle.fontFamily,
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '100%',
    letterSpacing: 0,
    width: 175,
    height: 37,
    borderRadius: 6,
    padding: '4px 14px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#2F58F2',
    color: '#FFFFFF',
    border: 'none',
    cursor: 'pointer',
    opacity: 1,
    flex: '0 0 auto',
  };

  const symbolAutocompleteContainerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    width: 220,
    background: '#FFFFFF',
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: 10,
    boxShadow: colors.shadow,
    overflow: 'hidden',
    zIndex: 10020,
    maxHeight: 320,
    overflowY: 'auto',
  };

  const symbolAutocompleteItemStyle = (active: boolean): React.CSSProperties => ({
    padding: '10px 12px',
    cursor: 'pointer',
    background: active ? colors.hoverBg : '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  });

  const symbolAutocompleteSymbolStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 800,
    fontSize: 13,
    color: '#0D0D0D',
  };

  const symbolAutocompleteNameStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 400,
    fontSize: 13,
    color: '#5A5A5A',
  };

  const symbolAutocompleteExchangeStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 500,
    fontSize: 12,
    color: '#7A7A7A',
  };

  const [portfolioId, setPortfolioId] = useState<number | null>(null);
  const [portfolioName, setPortfolioName] = useState<string>('');
  const [portfolios, setPortfolios] = useState<PortfolioDto[]>([]);

  const [draftSymbol, setDraftSymbol] = useState('');
  const [draftShares, setDraftShares] = useState('');
  const [draftOpenDate, setDraftOpenDate] = useState<string>(getTodayInputDate);
  const [draftBuyPrice, setDraftBuyPrice] = useState('');
  const [draftBuyPriceAlert, setDraftBuyPriceAlert] = useState('');
  const [draftSellPriceAlert, setDraftSellPriceAlert] = useState('');
  const [draftCurrentPrice, setDraftCurrentPrice] = useState<number | null>(null);
  const [symbolSearchBusy, setSymbolSearchBusy] = useState(false);
  const [showSymbolAutocomplete, setShowSymbolAutocomplete] = useState(false);
  const [symbolSelectedIndex, setSymbolSelectedIndex] = useState(-1);
  const [remoteStockResults, setRemoteStockResults] = useState<StockListItem[]>([]);

  const symbolAutocompleteRef = useRef<HTMLDivElement | null>(null);
  const symbolInputRef = useRef<HTMLInputElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  const uniqueStocksList = useMemo(() => {
    const list = stocksList as StockListItem[];
    const excluded = new Set(['MAG7']);
    const seen = new Set<string>();
    return list.filter((stock) => {
      if (excluded.has(stock.symbol)) return false;
      const key = normalizeSymbol(stock.symbol);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, []);

  const filteredStocks = useMemo(() => {
    const raw = draftSymbol.trim();
    if (!raw) return [] as StockListItem[];

    const query = raw.toLowerCase();
    const querySym = normalizeSearchSymbolInput(raw);
    const normalizedQuery = query.replace(/[^a-z0-9]+/g, ' ').trim();

    const score = (stock: StockListItem): number => {
      const sym = normalizeSymbol(stock.symbol);
      const name = String(stock.name || '').toLowerCase();
      const normalizedName = name.replace(/[^a-z0-9]+/g, ' ').trim();
      const cat = String(stock.category || '').toLowerCase();

      if (sym === 'V' && querySym.startsWith('VIS')) return 1100;
      if (sym === querySym) return 1000;
      if (normalizedQuery && normalizedName === normalizedQuery) return 980;
      if (normalizedQuery && normalizedName.startsWith(normalizedQuery)) return 950;
      if (sym.startsWith(querySym)) return 900;
      if (sym.includes(querySym)) return 800;
      if (name.includes(query)) return 600;
      if (cat.includes(query)) return 500;
      return 0;
    };

    const merged = [...remoteStockResults, ...uniqueStocksList];
    const seen = new Set<string>();

    return merged
      .filter((stock) => {
        const key = normalizeSymbol(stock.symbol);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((s) => ({ stock: s, score: score(s) }))
      .filter((x) => x.score > 0)
      .sort((a, b) =>
        (b.score - a.score) ||
        (getExchangeSearchPriority(a.stock.exchange) - getExchangeSearchPriority(b.stock.exchange)) ||
        a.stock.symbol.localeCompare(b.stock.symbol)
      )
      .slice(0, 25)
      .map((x) => x.stock);
  }, [draftSymbol, remoteStockResults, uniqueStocksList]);

  useEffect(() => {
    if (!open) return;
    const raw = draftSymbol.trim();
    if (!raw) {
      setRemoteStockResults([]);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      const queries = Array.from(new Set([
        raw,
        normalizeSearchSymbolInput(raw),
        normalizeSymbol(raw).replace(/\s+/g, '-'),
      ].map((value) => String(value || '').trim()).filter(Boolean)));

      try {
        const batches = await Promise.all(queries.map((queryText) => searchStocks(queryText, 50).catch(() => [])));
        if (cancelled) return;

        const mapped = batches.flat().map((result) => {
          const sector = String(result.sector || '').trim();
          const industry = String(result.industry || '').trim();
          const exchange = String(result.exchangeShortName || result.stockExchange || result.exchange || '').trim();
          const exchangeName = String(result.stockExchange || result.exchange || '').trim();
          return {
            symbol: String(result.symbol || '').trim(),
            name: String(result.name || '').trim(),
            exchange,
            exchangeName: exchangeName !== exchange ? exchangeName : '',
            category: sector && industry ? `${sector} / ${industry}` : (sector || industry || ''),
          };
        }).filter((result) => result.symbol);

        setRemoteStockResults(Array.from(new Map(mapped.map((row) => [normalizeSymbol(row.symbol), row])).values()));
      } catch {
        if (!cancelled) setRemoteStockResults([]);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [draftSymbol, open]);

  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (event: MouseEvent) => {
      const root = symbolAutocompleteRef.current;
      const input = symbolInputRef.current;
      const target = event.target as Node;
      if (root && root.contains(target)) return;
      if (input && input.contains(target)) return;
      setShowSymbolAutocomplete(false);
      setSymbolSelectedIndex(-1);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const runSymbolSearch = async (maybeSymbol?: string) => {
    const raw = maybeSymbol ?? draftSymbol;
    let sym = normalizeSearchSymbolInput(raw);
    if (!sym) {
      onNotify?.('Please enter a stock symbol/name.', 'error');
      return;
    }

    const queryCandidates = Array.from(new Set([
      sym,
      normalizeSymbol(raw),
      normalizeSymbol(raw).replace(/\s+/g, '-'),
    ].map((value) => String(value || '').trim()).filter(Boolean)));

    setDraftSymbol(sym);
    setShowSymbolAutocomplete(false);
    setSymbolSelectedIndex(-1);

    setSymbolSearchBusy(true);
    try {
      for (const queryText of queryCandidates) {
        const matches = await searchStocks(queryText, 50).catch(() => []);
        const exact = matches.find((item) => {
          const itemSymbol = normalizeSymbol(item.symbol);
          const itemCompact = itemSymbol.replace(/[.\-\s]/g, '');
          const queryCompact = normalizeSymbol(queryText).replace(/[.\-\s]/g, '');
          return itemSymbol === normalizeSymbol(queryText) || itemCompact === queryCompact;
        }) ?? matches[0];

        if (exact?.symbol) {
          sym = normalizeSymbol(exact.symbol);
          setDraftSymbol(sym);
          break;
        }
      }

      if (!authToken) return;

      const rows = await adminApi.getQuotesCache(authToken, [sym]);
      const price = Number(rows?.[0]?.currentPrice);
      if (Number.isFinite(price) && price > 0) setDraftCurrentPrice(price);
    } catch {
      // ignore
    } finally {
      setSymbolSearchBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    if (!authToken) {
      setPortfolioId(null);
      setPortfolioName('');
      setPortfolios([]);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const list = await adminApi.listMyPortfolios(authToken);
        if (cancelled) return;

        const available = (list ?? []).filter((p) => Number.isFinite(Number(p.id)));
        const def = available.find((p) => Boolean(p.isDefault)) ?? available[0] ?? null;
        const id = def?.id != null ? Number(def.id) : null;
        const name = String(def?.name || '').trim();
        setPortfolios(available);
        setPortfolioId(Number.isFinite(id) ? id : null);
        setPortfolioName(name);
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        onNotify?.(e?.message || 'Failed loading portfolios', 'error');
        setPortfolios([]);
        setPortfolioId(null);
        setPortfolioName('');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authToken, onNotify, onUnauthorized, open]);

  useEffect(() => {
    if (!open) return;
    const sym = prefillSymbol ? normalizeSymbol(prefillSymbol) : '';
    setDraftSymbol(sym);
    setDraftShares('');
    setDraftOpenDate(getTodayInputDate());
    setDraftBuyPrice('');
    setDraftBuyPriceAlert('');
    setDraftSellPriceAlert('');
    setDraftCurrentPrice(null);
    setShowSymbolAutocomplete(false);
    setSymbolSelectedIndex(-1);

    if (sym) void runSymbolSearch(sym);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillSymbol]);

  useEffect(() => {
    if (!open) return;

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      const modal = modalRef.current;
      if (!modal || !target) return;

      if (modal.contains(target)) {
        lastFocusedRef.current = target;
        return;
      }

      const fallback = modal.querySelector<HTMLElement>(
        'input, button, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      (lastFocusedRef.current ?? fallback)?.focus();
    };

    document.addEventListener('focusin', onFocusIn, true);
    return () => document.removeEventListener('focusin', onFocusIn, true);
  }, [open]);

  if (!open) return null;

  return (
    <div style={modalOverlayStyle} onClick={onClose} role="presentation">
      <div
        ref={modalRef}
        style={addHoldingModalStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Add Holdings to the Portfolio"
      >
        <div
          style={{
            height: 53,
            width: 'calc(100% + 40px)',
            marginLeft: -20,
            marginRight: -20,
            marginTop: -15,
            paddingLeft: 10,
            paddingRight: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <img
              src="/images/plus.svg"
              alt=""
              aria-hidden="true"
              style={{ width: 16, height: 16, display: 'block' }}
            />
            <span style={{ ...addTitleTextStyle, whiteSpace: 'nowrap', overflow: 'visible' }}>Add Holdings to the Portfolio</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            title="Close"
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
              color: '#0D0D0D',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 6,
            }}
          >
            <img src="/images/remove.svg" alt="Close" style={{ width: 24, height: 24, display: 'block' }} />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 25 }}>
          {portfolios.length > 1 ? (
            <div
              role="radiogroup"
              aria-label="Choose a portfolio"
              style={{
                width: 233,
                height: 89,
                boxSizing: 'border-box',
                border: '1px solid #7A7A7A',
                borderRadius: 6,
                overflow: 'hidden',
                background: '#FFFFFF',
                boxShadow: '1px 5px 5.5px -1px #7A7A7A40',
                fontFamily: 'Manrope, ui-sans-serif, system-ui, sans-serif',
                fontSize: 14,
                fontWeight: 500,
                lineHeight: '100%',
                letterSpacing: '1%',
              }}
            >
              {portfolios.map((portfolio, index) => {
                const selected = Number(portfolio.id) === portfolioId;
                return (
                  <label
                    key={portfolio.id}
                    onClick={() => {
                      setPortfolioId(Number(portfolio.id));
                      setPortfolioName(String(portfolio.name || '').trim());
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      height: 44,
                      padding: '0 8px',
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      borderBottom: index < portfolios.length - 1 ? '1px solid #E5E5E5' : 'none',
                      color: '#0D0D0D',
                    }}
                  >
                    <input
                      type="radio"
                      name="quick-add-portfolio"
                      value={portfolio.id}
                      checked={selected}
                      onChange={() => {
                        setPortfolioId(Number(portfolio.id));
                        setPortfolioName(String(portfolio.name || '').trim());
                      }}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        pointerEvents: 'none',
                      }}
                    />
                    <span
                      aria-hidden="true"
                      style={{
                        width: 22,
                        height: 22,
                        flex: '0 0 22px',
                        boxSizing: 'border-box',
                        borderRadius: '100px',
                        border: `1px solid ${selected ? '#2F58F2' : '#B8B8B8'}`,
                        background: selected ? '#2F58F2' : '#FFFFFF',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#FFFFFF',
                        fontSize: 14,
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {selected ? '✓' : ''}
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>{portfolio.name}</span>
                  </label>
                );
              })}
            </div>
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 18, alignItems: 'end', width: '100%' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 5 }}>
              <label style={addHoldingLabelStyle} htmlFor="portfolios-add-holding-symbol">
                Stock Name
              </label>
              <div style={{ position: 'relative', width: 220 }}>
                <input
                  ref={symbolInputRef}
                  id="portfolios-add-holding-symbol"
                  data-testid="portfolios-add-holding-symbol"
                  name="quick-add-portfolio-symbol"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  value={draftSymbol}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDraftSymbol(v);
                    setShowSymbolAutocomplete(v.trim().length > 0);
                    setSymbolSelectedIndex(-1);
                  }}
                  placeholder="Search Symbol"
                  style={addHoldingInputStyle}
                  onFocus={() => {
                    if (draftSymbol.trim()) setShowSymbolAutocomplete(true);
                  }}
                  onKeyDown={(e) => {
                    if (!showSymbolAutocomplete || filteredStocks.length === 0) {
                      if (e.key === 'Enter') void runSymbolSearch();
                      if (e.key === 'Escape') {
                        setShowSymbolAutocomplete(false);
                        setSymbolSelectedIndex(-1);
                      }
                      return;
                    }

                    switch (e.key) {
                      case 'ArrowDown':
                        e.preventDefault();
                        setSymbolSelectedIndex((prev) => (prev < filteredStocks.length - 1 ? prev + 1 : prev));
                        break;
                      case 'ArrowUp':
                        e.preventDefault();
                        setSymbolSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                        break;
                      case 'Enter':
                        e.preventDefault();
                        if (symbolSelectedIndex >= 0 && symbolSelectedIndex < filteredStocks.length) {
                          const s = filteredStocks[symbolSelectedIndex].symbol;
                          setDraftSymbol(s);
                          setShowSymbolAutocomplete(false);
                          setSymbolSelectedIndex(-1);
                          void runSymbolSearch(s);
                        } else {
                          void runSymbolSearch();
                        }
                        break;
                      case 'Escape':
                        setShowSymbolAutocomplete(false);
                        setSymbolSelectedIndex(-1);
                        break;
                    }
                  }}
                />

                {showSymbolAutocomplete && filteredStocks.length > 0 && (
                  <div ref={symbolAutocompleteRef} style={symbolAutocompleteContainerStyle}>
                    {filteredStocks.map((stock, index) => (
                      <div
                        key={`${stock.symbol}-${stock.exchange}-${index}`}
                        style={symbolAutocompleteItemStyle(index === symbolSelectedIndex)}
                        onClick={() => {
                          setDraftSymbol(stock.symbol);
                          setShowSymbolAutocomplete(false);
                          setSymbolSelectedIndex(-1);
                          void runSymbolSearch(stock.symbol);
                        }}
                        onMouseEnter={() => setSymbolSelectedIndex(index)}
                      >
                        <div style={symbolAutocompleteSymbolStyle}>
                          {stock.exchange ? `${stock.symbol} - ${stock.exchange}` : stock.symbol}
                        </div>
                        <div style={symbolAutocompleteNameStyle}>{stock.name}</div>
                        {(stock.exchangeName || stock.category) ? (
                          <div style={symbolAutocompleteExchangeStyle}>
                            {[stock.exchangeName, stock.category].filter(Boolean).join(' • ')}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              style={{ ...addHoldingSearchButtonStyle, justifySelf: 'end' }}
              id="portfolios-add-holding-search"
              data-testid="portfolios-add-holding-search"
              onClick={() => void runSymbolSearch()}
              disabled={symbolSearchBusy}
            >
              {symbolSearchBusy ? 'Searching' : 'Search'}
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 5 }}>
            <label style={addHoldingLabelStyle} htmlFor="quick-add-holding-shares">
              Number of Shares
            </label>
            <input
              id="quick-add-holding-shares"
              value={draftShares}
              onChange={(e) => setDraftShares(e.target.value)}
              placeholder=""
              inputMode="decimal"
              style={addHoldingInputStyle}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 5 }}>
            <label style={addHoldingLabelStyle} htmlFor="quick-add-holding-open-date">
              Buy Date
            </label>
            <input
              id="quick-add-holding-open-date"
              type="date"
              value={draftOpenDate}
              onChange={(e) => setDraftOpenDate(e.target.value)}
              style={addHoldingInputStyle}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={addHoldingLabelStyle} htmlFor="quick-add-holding-buy-price">
              Average Price per Share
            </label>
            <input
              id="quick-add-holding-buy-price"
              value={draftBuyPrice}
              onChange={(e) => setDraftBuyPrice(e.target.value)}
              placeholder=""
              inputMode="decimal"
              style={addHoldingInputStyle}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={addHoldingLabelStyle} htmlFor="quick-add-holding-buy-alert">
              Buy Price Alert
            </label>
            <input
              id="quick-add-holding-buy-alert"
              value={draftBuyPriceAlert}
              onChange={(e) => setDraftBuyPriceAlert(e.target.value)}
              placeholder=""
              inputMode="decimal"
              style={addHoldingInputStyle}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={addHoldingLabelStyle} htmlFor="quick-add-holding-sell-alert">
              Sell Price Alert
            </label>
            <input
              id="quick-add-holding-sell-alert"
              value={draftSellPriceAlert}
              onChange={(e) => setDraftSellPriceAlert(e.target.value)}
              placeholder=""
              inputMode="decimal"
              style={addHoldingInputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 18 }}>
          <button
            type="button"
            style={{ ...addHoldingAddButtonStyle, opacity: !portfolioId ? 0.65 : 1 }}
            onClick={() => {
              const symbol = String(draftSymbol || '').trim().toUpperCase();
              const shares = parseNumOrNull(draftShares);
              const buyPrice = parseNumOrNull(draftBuyPrice);
              const buyPriceAlertRaw = parseNumOrNull(draftBuyPriceAlert);
              const sellPriceAlertRaw = parseNumOrNull(draftSellPriceAlert);
              const buyPriceAlert = buyPriceAlertRaw != null && buyPriceAlertRaw > 0 ? buyPriceAlertRaw : null;
              const sellPriceAlert = sellPriceAlertRaw != null && sellPriceAlertRaw > 0 ? sellPriceAlertRaw : null;
              const openDateUtc = inputDateToUtcIso(draftOpenDate);

              if (!symbol) {
                onNotify?.('Please enter a stock symbol/name.', 'error');
                return;
              }
              if (!shares || shares <= 0) {
                onNotify?.('Please enter number of shares.', 'error');
                return;
              }
              if (!buyPrice || buyPrice <= 0) {
                onNotify?.('Please enter average price per share.', 'error');
                return;
              }
              if (!openDateUtc) {
                onNotify?.('Please select buy date.', 'error');
                return;
              }
              if (!authToken) {
                onNotify?.('You must be logged in to add/edit holdings.', 'error');
                return;
              }
              if (!portfolioId) {
                onNotify?.('Please select a portfolio first.', 'error');
                return;
              }

              void (async () => {
                let resolvedCurrentPrice: number | null = draftCurrentPrice && draftCurrentPrice > 0 ? draftCurrentPrice : null;
                if (!resolvedCurrentPrice) {
                  try {
                    const q = await getStockQuote(symbol);
                    const p = Number(q?.price);
                    if (Number.isFinite(p) && p > 0) resolvedCurrentPrice = p;
                  } catch {
                    // ignore
                  }
                }

                const currentPrice = resolvedCurrentPrice && resolvedCurrentPrice > 0 ? resolvedCurrentPrice : buyPrice;

                try {
                  const res = await adminApi.upsertMyPortfolioHolding(authToken, {
                    id: null,
                    portfolioId,
                    symbol,
                    shares,
                    buyPrice,
                    buyDateUtc: openDateUtc,
                    currentPrice,
                    buyPriceAlert,
                    sellPriceAlert,
                    status: 'Not Reached',
                    isClosed: false,
                  });

                  const savedId = Number((res as any)?.id);
                  if (!Number.isFinite(savedId) || savedId <= 0) {
                    onNotify?.('Saved, but missing holding id.', 'error');
                    onClose();
                    return;
                  }

                  onClose();
                  onAdded({ holdingId: savedId, portfolioName: portfolioName || 'Portfolio' });
                } catch (e: any) {
                  if (isUnauthorized(e)) {
                    onUnauthorized?.();
                    return;
                  }
                  onNotify?.(e?.message || 'Failed saving holding', 'error');
                }
              })();
            }}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickAddPortfolioHoldingModal;
